import { FormattingRule } from '@ardoq/data-model';
import {
  APIFieldType,
  FilterInfoOperator,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import type { Formatting } from './types';
import {
  dateRangeOperations,
  getDateRangeType,
  getStartDateName,
} from '@ardoq/date-range';
import { getFieldLabelOrName } from './utils';
import { type I18nLocale, t } from '@ardoq/i18n';

type RuleId = string;
type WarningKey = string;
type Message = string;

export type RuleNumberWarningsMap = Record<WarningKey, Message>;

/** Resolves a field name to its type, unwrapping combined date range fields. */
const resolveFieldType = (
  fieldTypesByName: Record<string, APIFieldType>,
  fieldName: string
): APIFieldType | null => {
  if (dateRangeOperations.isCombinedDateRangeFieldNames(fieldName)) {
    const startFieldType = fieldTypesByName[getStartDateName(fieldName)];
    return startFieldType ? getDateRangeType(startFieldType) : null;
  }
  return fieldTypesByName[fieldName] ?? null;
};

/** Checks if a rule targets a number field. */
const isNumberFieldRule = (
  fieldTypesByName: Record<string, APIFieldType>,
  rule: FormattingRule
): boolean => {
  if (!('field' in rule) || !rule.field) {
    return false;
  }

  return resolveFieldType(fieldTypesByName, rule.field) === APIFieldType.NUMBER;
};

/** Builds a unique key for storing/retrieving warnings. */
export const buildNumberWarningKey = (
  field: string,
  comparator: string,
  value: Partial<FormattingRule>['value']
): WarningKey => {
  // values with arrays (between, not between) are joined with a comma
  if (Array.isArray(value)) {
    return `${field}::${comparator}::${value[0]},${value[1]}`;
  }

  return `${field}::${comparator}::${value}`;
};

/** Represents a numeric range with inclusive/exclusive boundaries. */
type NumberRange = {
  field: string;
  min: number; // -Infinity for open ranges
  max: number; // +Infinity for open ranges
  // If minInclusive is false, the min value is excluded
  // If minInclusive is true, the min value is included
  minInclusive: boolean; // true => [m, n], false => (m, n)
  // If maxInclusive is false, the max value is excluded
  // If maxInclusive is true, the max value is included
  maxInclusive: boolean; // true => [m, n], false => (m, n)
  ruleId: string;
};

/** Converts a formatting rule into a numeric range representation. */
const ruleToRanges = (rule: FormattingRule): NumberRange[] => {
  if (
    !('field' in rule) ||
    !rule.field ||
    !rule.comparator ||
    rule.value === null
  ) {
    return [];
  }

  const { field, comparator, value, __id } = rule;

  // Single value operators
  if (typeof value === 'number') {
    switch (comparator) {
      case FilterInfoOperator.EQUALS:
        return [
          {
            field,
            min: value,
            max: value,
            minInclusive: true,
            maxInclusive: true,
            ruleId: __id,
          },
        ];

      case FilterInfoOperator.GREATER_THAN:
        return [
          {
            field,
            min: value,
            max: Infinity,
            minInclusive: false,
            maxInclusive: false,
            ruleId: __id,
          },
        ];

      case FormattingRuleOperator.GREATER_THAN_OR_EQUAL_TO:
        return [
          {
            field,
            min: value,
            max: Infinity,
            minInclusive: true,
            maxInclusive: false,
            ruleId: __id,
          },
        ];

      case FilterInfoOperator.LESS_THAN:
        return [
          {
            field,
            min: -Infinity,
            max: value,
            minInclusive: false,
            maxInclusive: false,
            ruleId: __id,
          },
        ];

      case FormattingRuleOperator.LESS_THAN_OR_EQUAL_TO:
        return [
          {
            field,
            min: -Infinity,
            max: value,
            minInclusive: false,
            maxInclusive: true,
            ruleId: __id,
          },
        ];
    }
  }

  const tupleValue = Array.isArray(value) && value.length === 2 ? value : null;
  const [start, end] = tupleValue ?? [NaN, NaN];

  // Range operators
  if (
    typeof start === 'number' &&
    typeof end === 'number' &&
    !isNaN(start) &&
    !isNaN(end)
  ) {
    if (comparator === FormattingRuleOperator.BETWEEN) {
      return [
        {
          field,
          min: start,
          max: end,
          minInclusive: true,
          maxInclusive: true,
          ruleId: __id,
        },
      ];
    }

    if (comparator === FormattingRuleOperator.NOT_BETWEEN) {
      return [
        {
          field,
          min: -Infinity,
          max: start,
          minInclusive: false,
          maxInclusive: true,
          ruleId: __id,
        },
        {
          field,
          min: end,
          max: Infinity,
          minInclusive: true,
          maxInclusive: false,
          ruleId: __id,
        },
      ];
    }
  }

  return [];
};

/** Normalizes open-ended ranges for consistent comparison. */
const normalizeRange = (range: NumberRange): NumberRange => {
  // Normalize > X and >= X
  if (range.max === Infinity) {
    return {
      ...range,
      minInclusive: true,
    };
  }

  // Normalize < X and <= X
  if (range.min === -Infinity) {
    return {
      ...range,
      maxInclusive: true,
    };
  }

  return range;
};

/** Returns true if two ranges are exactly equal. */
const rangesAreEqual = (a: NumberRange, b: NumberRange): boolean => {
  const normalizedA = normalizeRange(a);
  const normalizedB = normalizeRange(b);

  return (
    normalizedA.field === normalizedB.field &&
    normalizedA.min === normalizedB.min &&
    normalizedA.max === normalizedB.max &&
    normalizedA.minInclusive === normalizedB.minInclusive &&
    normalizedA.maxInclusive === normalizedB.maxInclusive
  );
};

/** Returns true if two ranges have any overlapping values. */
const rangesOverlap = (range1: NumberRange, range2: NumberRange): boolean => {
  if (range1.field !== range2.field) {
    return false;
  }

  // Check if range1 ends before range2 starts
  if (
    range1.max < range2.min ||
    (range1.max === range2.min && !(range1.maxInclusive && range2.minInclusive))
  ) {
    return false;
  }

  // Check if range1 starts after range2 ends
  if (
    range1.min > range2.max ||
    (range1.min === range2.max && !(range1.minInclusive && range2.maxInclusive))
  ) {
    return false;
  }
  // Ranges overlap
  return true;
};

/** Builds a human-readable description of a range. */
const describeRange = (
  i18nLocale: I18nLocale,
  range: NumberRange,
  formattingByRule: Partial<Record<string, Formatting>>
): string => {
  const fieldLabel = getFieldLabelOrName(formattingByRule, range.field);

  // Single value - equals
  if (range.min === range.max && range.minInclusive && range.maxInclusive) {
    return t(
      i18nLocale,
      'perspectives:numberWarnings.equals',
      { fieldLabel, value: range.min },
      '{{fieldLabel}} equals {{value}}'
    );
  }

  // Between (inclusive range)
  if (
    range.min !== -Infinity &&
    range.max !== Infinity &&
    range.minInclusive &&
    range.maxInclusive
  ) {
    return t(
      i18nLocale,
      'perspectives:numberWarnings.between',
      { fieldLabel, min: range.min, max: range.max },
      '{{fieldLabel}} between {{min}} to {{max}}'
    );
  }

  // Not between (exclusive range)
  if (
    range.min !== -Infinity &&
    range.max !== Infinity &&
    !range.minInclusive &&
    !range.maxInclusive
  ) {
    return t(
      i18nLocale,
      'perspectives:numberWarnings.notBetween',
      { fieldLabel, min: range.min, max: range.max },
      '{{fieldLabel}} not between {{min}} to {{max}}'
    );
  }

  // Greater than
  if (range.min !== -Infinity && range.max === Infinity) {
    return range.minInclusive
      ? t(
          i18nLocale,
          'perspectives:numberWarnings.greaterThanOrEqualTo',
          { fieldLabel, value: range.min },
          '{{fieldLabel}} greater than or equal to {{value}}'
        )
      : t(
          i18nLocale,
          'perspectives:numberWarnings.greaterThan',
          { fieldLabel, value: range.min },
          '{{fieldLabel}} greater than {{value}}'
        );
  }

  // Less than
  if (range.min === -Infinity && range.max !== Infinity) {
    return range.maxInclusive
      ? t(
          i18nLocale,
          'perspectives:numberWarnings.lessThanOrEqualTo',
          { fieldLabel, value: range.max },
          '{{fieldLabel}} less than or equal to {{value}}'
        )
      : t(
          i18nLocale,
          'perspectives:numberWarnings.lessThan',
          { fieldLabel, value: range.max },
          '{{fieldLabel}} less than {{value}}'
        );
  }

  return t(
    i18nLocale,
    'perspectives:numberWarnings.inRange',
    { fieldLabel },
    '{{fieldLabel}} in range'
  );
};

/**
 * Groups ranges by field name.
 * Only ranges targeting the same field can conflict with each other.
 */
const groupRangesByField = (
  ranges: NumberRange[]
): Map<string, NumberRange[]> => {
  const rangesByField = new Map<string, NumberRange[]>();

  for (const range of ranges) {
    const fieldRanges = rangesByField.get(range.field);

    if (fieldRanges) {
      fieldRanges.push(range);
    } else {
      rangesByField.set(range.field, [range]);
    }
  }

  return rangesByField;
};

/**
 * Checks a single range against other ranges for the same field.
 * Returns the first conflict found, or null if no conflicts exist.
 */
const findConflictForRange = (
  i18nLocale: I18nLocale,
  currentRange: NumberRange,
  otherRanges: NumberRange[],
  formattingByRuleAndField: Record<string, Partial<Record<string, Formatting>>>
): string | null => {
  for (const otherRange of otherRanges) {
    // Skip comparing the range with itself
    if (currentRange.ruleId === otherRange.ruleId) {
      continue;
    }

    // Exact duplicate — identical ranges
    if (rangesAreEqual(otherRange, currentRange)) {
      const formattingByRule =
        formattingByRuleAndField[otherRange.ruleId] || {};
      const otherDescription = describeRange(
        i18nLocale,
        otherRange,
        formattingByRule
      );

      return t(
        i18nLocale,
        'perspectives:numberWarnings.duplicate',
        { description: otherDescription },
        'This conditional format is a duplicate of an existing one - {{description}}'
      );
    }

    // Any other intersection is overlap
    if (rangesOverlap(otherRange, currentRange)) {
      const formattingByRule =
        formattingByRuleAndField[otherRange.ruleId] || {};
      const otherDescription = describeRange(
        i18nLocale,
        otherRange,
        formattingByRule
      );

      return t(
        i18nLocale,
        'perspectives:numberWarnings.overlap',
        { description: otherDescription },
        'This conditional format overlaps with an existing one - {{description}}'
      );
    }
  }

  return null;
};

/** Detects overlapping or duplicate number field formatting rules and returns warnings. */
export const buildNumberWarningsMap = (
  i18nLocale: I18nLocale,
  fieldTypesByName: Record<string, APIFieldType>,
  rules: FormattingRule[],
  formattingByRuleAndField: Record<string, Partial<Record<string, Formatting>>>
): Record<RuleId, RuleNumberWarningsMap> => {
  const numberWarningsByKey: Record<RuleId, RuleNumberWarningsMap> = {};

  const numberRules = rules.filter(rule =>
    isNumberFieldRule(fieldTypesByName, rule)
  );
  const ranges: NumberRange[] = numberRules.flatMap(rule => ruleToRanges(rule));

  // Group ranges by field - only same-field ranges can conflict
  const rangesByField = groupRangesByField(ranges);

  // Check each range within its field group for conflicts
  for (const fieldRanges of rangesByField.values()) {
    for (const currentRange of fieldRanges) {
      const warningMessage = findConflictForRange(
        i18nLocale,
        currentRange,
        fieldRanges,
        formattingByRuleAndField
      );

      if (warningMessage) {
        const currentRule = numberRules.find(
          r => r.__id === currentRange.ruleId
        );

        if (currentRule && 'field' in currentRule && currentRule.field) {
          const warningKey = buildNumberWarningKey(
            currentRule.field,
            String(currentRule.comparator),
            currentRule.value as number
          );

          if (!numberWarningsByKey[currentRange.ruleId]) {
            numberWarningsByKey[currentRange.ruleId] = {};
          }

          numberWarningsByKey[currentRange.ruleId][warningKey] = warningMessage;
        }
      }
    }
  }

  return numberWarningsByKey;
};
